import React, { useRef } from "react";

import Slider from "react-slick";
import { useTranslation } from "react-i18next";
import { isMobile } from "react-device-detect";
import { useSelector } from "react-redux";
import { Button, Grid, Typography } from "@mui/material";

import useStyles from "./Lobby.styles";
import { ReactComponent as LeftArrowIcon } from "../../../assets/images/leftArrow.svg";
import { ReactComponent as RightArrowIcon } from "../../../assets/images/rightArrow.svg";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./sports.css";

const SportsCarousel = () => {
  const sliderRef = useRef(null);
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const classes = useStyles();

  // Get data from Redux store
  const {
    data: sportsData,
    loading,
    error
  } = useSelector((state) => state.sportsBanner?.data);

  // near top of component, after you get sportsData:
  const presets = [
    "#FFA500",
    "#FF4B2B",
    "#FF0066",
    "#00C9A7",
    "#4A00E0",
    "#FFD200",
    "#FF7F50",
    "#1E90FF",
    "#32CD32",
    "#FF1493",
    "#00BFFF",
    "#8A2BE2"
  ];

  const DEFAULT_SPORT_IMAGE = "/images/defaultSportsCard1.png";

  const shuffle = (arr) => {
    const a = arr.slice();
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  // generate exactly `n` unique colors; if presets too small, generate random hex colors to fill
  const uniqueColors = React.useMemo(() => {
    const n = (sportsData && sportsData.length) || 0;
    if (n === 0) return [];

    const shuffled = shuffle(presets);

    if (shuffled.length >= n) {
      return shuffled.slice(0, n);
    }

    // fill remaining with random colors
    const colors = [...shuffled];
    while (colors.length < n) {
      const rand =
        "#" +
        Math.floor(Math.random() * 0xffffff)
          .toString(16)
          .padStart(6, "0");
      if (!colors.includes(rand)) colors.push(rand);
    }
    return colors;
  }, [sportsData?.length]); // regenerate only when number of cards changes

  // Handle navigation
  const handleSportClick = (redirectUrl) => {
    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
  };

  // Handle keyboard navigation

  function SampleNextArrow(props) {
    const { onClick, currentSlide } = props;
    const isLastSlide = currentSlide >= (sportsData?.length - 1 || 0);

    return (
      <div className={`${classes.arrowbutton} ${classes.rightArrow}`}>
        <Button
          onClick={onClick}
          className={classes.frvbtnstyle}
          disabled={isLastSlide}
        >
          <RightArrowIcon className={isLastSlide ? "inactive" : "active"} />
        </Button>
      </div>
    );
  }

  function SamplePrevArrow(props) {
    const { onClick, currentSlide } = props;
    return (
      <div className={`${classes.arrowbutton} ${classes.leftArrow}`}>
        <Button
          onClick={onClick}
          className={classes.prvbtnstyle}
          disabled={currentSlide === 0}
        >
          <LeftArrowIcon
            className={currentSlide === 0 ? "inactive" : "active"}
          />
        </Button>
      </div>
    );
  }

  const settings = {
    dots: false,
    infinite: false,
    draggable: false,
    speed: 500,
    slidesToShow: isMobile ? 3 : 7,
    slidesToScroll: 2,
    arrows: true,
    nextArrow: <SampleNextArrow />,
    prevArrow: <SamplePrevArrow />,
    rows: 1, // Force single row
    slidesPerRow: 1, // One slide per cell

    responsive: [
      {
        breakpoint: 1024,
        settings: { slidesToShow: 6 }
      },
      {
        breakpoint: 768,
        settings: { slidesToShow: 4 }
      },
      {
        breakpoint: 480,
        settings: { slidesToShow: 3 }
      }
    ]
  };

  // Show loading state
  if (loading) {
    return (
      <div className="rounded-xl">
        <Grid className={classes.innerHeadingLeft}>
          <img
            src="/images/providerSports.png"
            className={classes.heartIcon}
            alt="Sports Icon"
          />
          <Typography variant="h6" className="text-white font-semibold">
            {t("LOBBY.TOP_SPORTS")}
          </Typography>
        </Grid>
        <div className="text-white text-center py-10">Loading sports...</div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="rounded-xl">
        <Grid className={classes.innerHeadingLeft}>
          <img
            src="/images/providerSports.png"
            className={classes.heartIcon}
            alt="Sports Icon"
          />
          <Typography variant="h6" className="text-white font-semibold">
            {t("LOBBY.TOP_SPORTS")}
          </Typography>
        </Grid>
        <div className="text-red-500 text-center py-10">
          Failed to load sports
        </div>
      </div>
    );
  }

  // Show empty state
  if (!sportsData || sportsData.length === 0) {
    return (
      <div className="rounded-xl">
        <Grid className={classes.innerHeadingLeft}>
          <img
            src="/images/providerSports.png"
            className={classes.heartIcon}
            alt="Sports Icon"
          />
          <Typography variant="h6" gutterBottom>
            {t("LOBBY.TOP_SPORTS")}
          </Typography>
        </Grid>
        <div className="text-white text-center py-10">No sports available</div>
      </div>
    );
  }
  // console.log("sportsData", sportsData);

  return (
    <div className="rounded-xl">
      {/* Header */}
      <Grid className={classes.innerHeadingLeft}>
        <img
          src="/images/providerSports.png"
          className={classes.heartIcon}
          alt="Sports Icon"
        />
        <Typography variant="h6">{t("LOBBY.TOP_SPORTS")}</Typography>
      </Grid>

      {/* Carousel */}
      <Slider ref={sliderRef} {...settings}>
        {sportsData.map((sport, index) => {
          const baseColor = uniqueColors[index] || "#FFA500";
          const sportName = sport.name?.[lang] || sport.name?.en; // fallback english
          const sportSlug = sport.slug?.[lang] || sport.slug?.en;
          const redirectUrl = `/sports/${sportSlug}`;
          return (
            <div key={sport.id} className="pr-2">
              <button
                className="relative rounded-xl overflow-hidden cursor-pointer"
                onClick={() => handleSportClick(redirectUrl)}
              >
                {/* IMAGE */}
                <img
                  src={
                    (isMobile ? sport?.mobileImageUrl : sport?.imageUrl) ||
                    DEFAULT_SPORT_IMAGE
                  }
                  alt={sportName}
                  className="w-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = DEFAULT_SPORT_IMAGE;
                  }}
                />

                {/* CINEMATIC GRADIENT */}
                <div
                  className="absolute bottom-0 w-full h-32 rounded-b-xl"
                  style={{
                    background: `linear-gradient(
                      to top,
                      ${baseColor} 0%,
                      ${baseColor}99 35%,
                      rgba(0,0,0,0.3) 70%,
                      transparent 100%
                    )`
                  }}
                ></div>

                {/* TEXT */}
                <div className="absolute bottom-4 w-full flex justify-center">
                  <p
                    className="
      text-white
      w-[120px]
      text-[12px] sm:text-[14px] md:text-[16px]
      leading-[16px] sm:leading-[18px] md:leading-[20px]
      font-extrabold uppercase
      text-center
      drop-shadow-[0_3px_8px_rgba(0,0,0,0.9)]
    "
                    style={{
                      hyphens: "manual",
                      overflowWrap: "break-word"
                    }}
                    dangerouslySetInnerHTML={{
                      __html: sportName.replace(/(.{3})/g, "$1&shy;")
                    }}
                  />
                </div>
              </button>
            </div>
          );
        })}
      </Slider>
    </div>
  );
};

export default SportsCarousel;
